import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MonthlyRevenue, MonthlyRevenueDTO } from "@models/monthly-revenue";
import { OrdersPaymentMethodSummary } from "@models/orders-payment-method-summary";
import { SalesChannelSummary } from "@models/sales-channel-summary";
import { environment } from "../../environments/environment";
import { DateTime } from "luxon";
import { catchError, map, Observable, of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class RevenueService {
  private readonly baseUrl = `${environment.API_BACKEND}/`;

  constructor(private readonly http: HttpClient) {}

  getOrganizerRevenue(
    startMonth?: Date,
    endMonth?: Date,
  ): Observable<MonthlyRevenue[]> {
    const startDateParam = startMonth
      ? DateTime.fromJSDate(startMonth)
      : DateTime.now().minus({ months: 12 });
    let params = new HttpParams().set(
      "start_month",
      startDateParam.toFormat("yyyy-MM"),
    );
    if (endMonth) {
      params = params.set(
        "end_month",
        DateTime.fromJSDate(endMonth).toFormat("yyyy-MM"),
      );
    }
    return this.http
      .get<MonthlyRevenueDTO>(`${this.baseUrl}/revenue_for_organizer`, {
        params,
      })
      .pipe(
        map((dto: MonthlyRevenueDTO): MonthlyRevenue[] =>
          Object.entries(dto)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([month, value]) => ({ month, value })),
        ),
      );
  }

  // Total do produtor agrupado por meio de pagamento (todos os eventos).
  // O backend responde 404 quando não há vendas; tratamos como lista vazia.
  getPaymentMethodsForOrganizer(): Observable<OrdersPaymentMethodSummary[]> {
    return this.http
      .get<
        OrdersPaymentMethodSummary[]
      >(`${this.baseUrl}/payment_methods_for_organizer`)
      .pipe(catchError(() => of([])));
  }

  // Total do produtor agrupado por canal de venda (online, pdv, affiliate).
  getSalesChannelsForOrganizer(): Observable<SalesChannelSummary[]> {
    return this.http
      .get<
        SalesChannelSummary[]
      >(`${this.baseUrl}/sales_channels_for_organizer`)
      .pipe(catchError(() => of([])));
  }

  getAdminRevenue(
    startMonth?: Date,
    endMonth?: Date,
  ): Observable<MonthlyRevenue[]> {
    const startDateParam = startMonth
      ? DateTime.fromJSDate(startMonth)
      : DateTime.now().minus({ months: 12 });
    let params = new HttpParams().set(
      "start_month",
      startDateParam.toFormat("yyyy-MM"),
    );
    if (endMonth) {
      params = params.set(
        "end_month",
        DateTime.fromJSDate(endMonth).toFormat("yyyy-MM"),
      );
    }
    return this.http
      .get<MonthlyRevenueDTO>(`${this.baseUrl}/revenue_for_admin`, {
        params,
      })
      .pipe(
        map((dto: MonthlyRevenueDTO): MonthlyRevenue[] =>
          Object.entries(dto)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([month, value]) => ({ month, value })),
        ),
      );
  }
}
